/* eslint-disable react/prop-types */
/**
 * InquiriesDesk — the admin dashboard for working the concierge inquiry
 * queue, at /staff/inquiries. The Staff page's DailyReport panel is the
 * glanceable digest; this is the sit-down surface for actually handling
 * the queue: an inbox-style split view where every inquiry opens with the
 * customer's full chat transcript beside it.
 *
 * Layout (all one responsive tree, no duplicated content):
 *   - lg+  : fixed-height two-pane split — inquiry list (360px) | detail.
 *   - xl+  : the detail pane splits again — conversation | context rail
 *            (request, details grid, Stephanie's internal notes).
 *   - <lg  : single pane; picking an inquiry pushes a full-screen detail
 *            with a back affordance, list state preserved.
 *
 * Data: GET /api/admin/concierge-inquiries (list + stats), and per-inquiry
 * GET /api/admin/support-threads/:thread_id for the linked transcript —
 * consults included, with the consult that filed this inquiry highlighted
 * and scrolled into view. Claim/resolve POST to the same endpoints the
 * Staff DailyReport uses; resolving emails the customer and updates their
 * trip card, so the composer says exactly that. Both composer buttons post
 * the message in the customer's chat as "Defera team" (one conversation
 * per customer, server/support-conversation.js); "Send, keep open" writes
 * without resolving (a question back, an update).
 *
 * Security posture: every route above is requireAdmin()-gated server-side.
 * The client-side role check only decides which UI to render.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Archive,
  CalendarClock,
  CheckCircle2,
  ChevronLeft,
  CircleDashed,
  Clock3,
  ExternalLink,
  Hand,
  Inbox,
  Loader2,
  Plane,
  RefreshCw,
  Reply,
  RotateCcw,
  Search,
  Send,
  ShieldAlert,
  Sparkles,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { User as UserEntity, FlightBooking } from '@/api/entities';
import FlightDetailsModal from '@/components/flight/FlightDetailsModal';
import { Button } from '@/components/ui/button';
import { PreviewMarkdown, ConsultTranscript } from '@/components/support/AdminSupportInbox';
import { getSpecialist } from '@/constants/conciergeSpecialists';
import { isDeliberateConfirm, UNDO_WINDOW_MS } from '@/utils/dismissConfirm';
import { LIST_REFRESH_MS, TRANSCRIPT_REFRESH_MS, sameTranscript } from '@/utils/inquiriesDeskRefresh';
import { listenForSignals } from '@/lib/realtimeSignals';
import VipBadge, { VipPill } from '@/components/admin/VipBadge';
import { isAdmin as isStrictAdmin } from '@/utils/roles';
import AccountDeletionCard from '@/components/admin/AccountDeletionCard';
import { scheduledDateLabel, teamToday } from '@/utils/scheduledRequestDate';

// Admin role check — mirrors Layout.jsx / Staff.jsx. Backed by JWT
// app_metadata.roles (users cannot self-edit those, unlike user_metadata).
function isCurrentUserAdmin(u) {
  if (!u) return false;
  const roles = u?.app_metadata?.roles || u?.appMetadata?.roles || [];
  const list = Array.isArray(roles) ? roles : (roles ? [roles] : []);
  // `concierge` is the desk-only role (the browser agent). It reaches this
  // queue and read-only trip data, nothing else — see src/utils/roles.js.
  return list.includes('admin') || list.includes('concierge') || u?.role === 'admin';
}

/** Mirrors ServiceInquiryCard.jsx CATEGORY_LABELS — the desk must headline
 *  a request the same way the customer's trip card does. */
const CATEGORY_LABELS = {
  seats: 'Seat request',
  baggage: 'Baggage request',
  upgrade: 'Cabin upgrade request',
  flight_change: 'Flight change request',
  miles: 'Miles request',
  gift_card: 'E-gift card redemption',
  contact: 'Contact page message',
  email: 'Email to the team',
  confirmation_release: 'Confirmation number release',
  account_deletion: 'Account deletion request',
  billing: 'Billing question',
  booking: 'Booking question',
  cancellation: 'Cancellation request',
  default_resolution: 'Trip resolution request',
  policy: 'Policy question',
  fare_rules: 'Fare question',
  feedback: 'Feedback',
  other: 'Service request',
};

const inquiryHeadline = (inq) => inq?.title || CATEGORY_LABELS[inq?.category] || 'Service request';
const customerName = (inq) => inq?.user_first_name
  || (inq?.user_email ? inq.user_email.split('@')[0] : 'Customer');

function formatRelativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const diff = Date.now() - then;
  if (diff < 60_000) return 'just now';
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatAbsolute(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** A customer who answered the team gets an answer on the request clock
 *  (server/concierge-inquiries.js REPLY_DUE_HOURS). */
const REPLY_DUE_MS = 24 * 3600_000;
const replyWaitingTooLong = (inq) => Boolean(
  inq?.customer_replied_at && Date.now() - new Date(inq.customer_replied_at).getTime() > REPLY_DUE_MS,
);

const isOverdue = (inq) => Boolean(
  (inq?.due_by
    && new Date(inq.due_by).getTime() < Date.now()
    && ['open', 'in_progress'].includes(inq.status))
  || replyWaitingTooLong(inq),
);

/** Needs a message from the team: open, or the customer answered us. */
const needsAnswer = (inq) => Boolean(
  inq && inq.status !== 'dismissed' && (['open', 'in_progress'].includes(inq.status) || inq.customer_replied_at),
);

const sameWords = (a, b) => String(a || '').replace(/\s+/g, ' ').trim() === String(b || '').replace(/\s+/g, ' ').trim();

/** What the server clears once the team writes to the customer (migration 181). */
const SPENT_REPLY_AND_DRAFT = Object.freeze({
  customer_replied_at: null,
  customer_reply_preview: null,
  stephanie_draft: null,
  stephanie_draft_notes: null,
  stephanie_draft_ready: null,
  stephanie_draft_at: null,
});

function dueLabel(inq) {
  if (!inq?.due_by) return '';
  const ms = new Date(inq.due_by).getTime() - Date.now();
  const hrs = Math.round(Math.abs(ms) / 3600000);
  if (ms < 0) return hrs < 1 ? 'just breached' : `${hrs}h overdue`;
  if (hrs < 1) return 'due within the hour';
  return `due in ${hrs}h`;
}

/** One status → one chip, everywhere on the page. Overdue is derived and
 *  wins over the stored status: a breached promise is the louder fact. */
function StatusChip({ inquiry, className = '' }) {
  const overdue = isOverdue(inquiry);
  const base = 'inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded border';
  // The customer answered the team: waiting on us, whatever the status says.
  if (inquiry?.customer_replied_at) {
    const late = replyWaitingTooLong(inquiry);
    return (
      <span className={`${base} ${late ? 'text-red-200 bg-red-500/15 border-red-400/25' : 'text-sky-200 bg-sky-500/15 border-sky-400/25'} ${className}`}>
        <Reply className="w-2.5 h-2.5" strokeWidth={2} />
        Replied {formatRelativeTime(inquiry.customer_replied_at)}
      </span>
    );
  }
  if (overdue) {
    return (
      <span className={`${base} text-red-200 bg-red-500/15 border-red-400/25 ${className}`}>
        <Clock3 className="w-2.5 h-2.5" strokeWidth={2} />
        {dueLabel(inquiry)}
      </span>
    );
  }
  if (inquiry?.status === 'resolved') {
    return (
      <span className={`${base} text-emerald-300/90 bg-emerald-500/12 border-emerald-400/25 ${className}`}>
        <CheckCircle2 className="w-2.5 h-2.5" strokeWidth={2} />
        Resolved
      </span>
    );
  }
  if (inquiry?.status === 'in_progress') {
    return (
      <span className={`${base} text-brand-200 bg-brand-500/15 border-brand-400/25 ${className}`}>
        <Loader2 className="w-2.5 h-2.5" strokeWidth={2} />
        In progress
      </span>
    );
  }
  if (inquiry?.status === 'dismissed') {
    // Admin-erased duplicate/misfile — the customer was never emailed and
    // the card is gone from their trip page.
    return (
      <span className={`${base} text-white/45 bg-white/[0.04] border-white/[0.08] ${className}`}>
        Dismissed
      </span>
    );
  }
  return (
    <span className={`${base} text-white/60 bg-white/[0.06] border-white/[0.10] ${className}`}>
      <CircleDashed className="w-2.5 h-2.5" strokeWidth={2} />
      Open
    </span>
  );
}

/** Initial-letter avatar for customer rows/bubbles (no profile photos). */
function CustomerDot({ inquiry, size = 'w-8 h-8 text-[12px]' }) {
  const letter = (customerName(inquiry)[0] || 'C').toUpperCase();
  return (
    <div className={`${size} rounded-full bg-brand-500/25 border border-brand-400/25 text-brand-100 font-semibold flex items-center justify-center flex-shrink-0`}>
      {letter}
    </div>
  );
}

// ---------------------------------------------------------------------------
// List pane
// ---------------------------------------------------------------------------

function InquiryRow({ inquiry, active, onSelect }) {
  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(inquiry.id)}
        className={`w-full text-left px-3.5 py-3 transition ${
          active ? 'bg-brand-500/[0.10]' : 'hover:bg-white/[0.035] active:bg-white/[0.06]'
        }`}
      >
        <div className="flex items-start gap-2.5 min-w-0">
          <CustomerDot inquiry={inquiry} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-[13.5px] font-medium text-white truncate">{customerName(inquiry)}</span>
              {/* The server marks VIP rows and puts the ones needing an answer first. */}
              {inquiry.vip ? <VipPill /> : <VipBadge userId={inquiry.user_id || null} />}
              {inquiry.booking_reference ? (
                <span className="text-[10.5px] text-emerald-300/80 flex-shrink-0">{inquiry.booking_reference}</span>
              ) : null}
              <span className="ml-auto text-[10.5px] text-white/35 flex-shrink-0">
                {formatRelativeTime(inquiry.created_at)}
              </span>
            </div>
            <div className="text-[13.5px] text-white/90 truncate mt-0.5 leading-snug">
              {inquiryHeadline(inquiry)}
            </div>
            <div className="text-[12.5px] text-white/55 line-clamp-1 mt-0.5 leading-snug">
              {inquiry.customer_question}
            </div>
            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
              <span className="text-[10px] uppercase tracking-wider font-medium text-amber-200/80 bg-amber-500/10 border border-amber-400/15 px-1.5 py-0.5 rounded">
                {(inquiry.category || 'other').replace(/_/g, ' ')}
              </span>
              <StatusChip inquiry={inquiry} />
            </div>
          </div>
        </div>
      </button>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Detail pane — context rail blocks
// ---------------------------------------------------------------------------

/** The ticket-style facts grid: one label/value row per known fact. */
function DetailsCard({ inquiry, onOpenTrip }) {
  const rows = [
    ['Request', inquiryHeadline(inquiry)],
    ['Category', CATEGORY_LABELS[inquiry.category] || (inquiry.category || 'other').replace(/_/g, ' ')],
    ['Customer', customerName(inquiry)],
    ['Email', inquiry.user_email || '—'],
    ['Trip', inquiry.booking_reference || '—', Boolean(inquiry.booking_reference)],
    ['Filed', formatAbsolute(inquiry.created_at)],
    // The countdown only means something while the promise is still open.
    ['Due', inquiry.due_by
      ? `${formatAbsolute(inquiry.due_by)}${['open', 'in_progress'].includes(inquiry.status) ? ` (${dueLabel(inquiry)})` : ''}`
      : '—'],
  ];
  if (inquiry.scheduled_for) rows.push(['Scheduled', scheduledDateLabel(inquiry.scheduled_for, { withYear: true }) || inquiry.scheduled_for]);
  if (inquiry.priority === 'low') rows.push(['Priority', 'Low']);
  if (inquiry.resolved_at) rows.push(['Resolved', formatAbsolute(inquiry.resolved_at)]);
  // status='dismissed' is the admin silent-erase; customer dismissals of a
  // resolved card set dismissed_at while status stays 'resolved'.
  if (inquiry.dismissed_at) {
    rows.push(['Card', inquiry.status === 'dismissed' ? 'Dismissed by admin (no email sent)' : 'Dismissed by customer']);
  }
  return (
    <section className="rounded-2xl border border-white/[0.07] bg-white/[0.025] p-3.5">
      <div className="flex items-center justify-between gap-2 mb-2.5">
        <h3 className="text-[10.5px] uppercase tracking-wider font-semibold text-white/40">Details</h3>
        <StatusChip inquiry={inquiry} />
      </div>
      <dl className="space-y-1.5">
        {rows.map(([label, value, openable]) => (
          <div key={label} className="flex items-baseline gap-3 min-w-0">
            <dt className="w-[72px] flex-shrink-0 text-[11.5px] text-white/50">{label}</dt>
            <dd className="text-[13px] text-white/85 leading-snug min-w-0 break-words">
              {openable && onOpenTrip ? (
                <button
                  type="button"
                  onClick={onOpenTrip}
                  className="inline-flex items-center gap-1.5 text-brand-200/95 hover:text-white underline underline-offset-2 decoration-white/20 hover:decoration-white/60 transition-colors"
                >
                  {value}
                  <ExternalLink className="w-3 h-3 opacity-70" />
                </button>
              ) : value}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/**
 * Schedule an open request for a specific day (admins only). The date is a
 * promise: the customer's trip card shows it in place of the usual timing,
 * the concierge quotes it, and the due date moves to that day. Clearing it
 * puts the usual timing back on the card.
 */
function ScheduleCard({ inquiry, onSchedule, saving, error }) {
  const [date, setDate] = useState(inquiry.scheduled_for || '');
  useEffect(() => { setDate(inquiry.scheduled_for || ''); }, [inquiry.id, inquiry.scheduled_for]);
  const current = scheduledDateLabel(inquiry.scheduled_for, { withYear: true });
  const changed = Boolean(date) && date !== (inquiry.scheduled_for || '');
  return (
    <section className="rounded-2xl border border-white/[0.07] bg-white/[0.025] p-3.5" data-testid="schedule-card">
      <div className="flex items-center gap-2 mb-1.5">
        <CalendarClock className="w-3.5 h-3.5 text-white/45" strokeWidth={1.75} />
        <h3 className="text-[10.5px] uppercase tracking-wider font-semibold text-white/40">Scheduled date</h3>
      </div>
      <p className="text-[12px] text-white/60 leading-relaxed">
        {current
          ? `Promised for ${current}. The customer sees this date on their trip page, and the concierge quotes it.`
          : 'Set a day for this request. The customer sees it on their trip page instead of the usual timing, and the concierge quotes it.'}
      </p>
      <div className="mt-2.5 flex items-center gap-2">
        <input
          type="date"
          aria-label="Scheduled date"
          value={date}
          min={teamToday()}
          onChange={(e) => setDate(e.target.value)}
          className="min-w-0 flex-1 h-8 rounded-lg border border-white/[0.1] bg-black/40 px-2 text-[12.5px] text-white [color-scheme:dark]"
        />
        <button
          type="button"
          onClick={() => onSchedule(date)}
          disabled={!changed || saving}
          className="h-8 px-3 rounded-full bg-white text-black text-[12px] font-medium disabled:opacity-40"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        {inquiry.scheduled_for ? (
          <button
            type="button"
            onClick={() => onSchedule(null)}
            disabled={saving}
            className="h-8 px-2.5 rounded-full border border-white/[0.1] text-[12px] text-white/70 hover:text-white disabled:opacity-40"
          >
            Clear
          </button>
        ) : null}
      </div>
      {error ? <p className="mt-2 text-[12px] text-red-200/90">{error}</p> : null}
    </section>
  );
}

/** Stephanie's candid write-up. INTERNAL — the customer's payload omits
 *  these columns on purpose; never render this on a customer surface.
 *
 *  `whitespace-pre-line` matters: these used to be single-line strings (her
 *  SUMMARY:/RECOMMENDATION: trailer is one line each), so a plain <p> was
 *  fine. Urgent tickets now carry multi-line, numbered write-ups — the
 *  remediation batch of 2026-09-12 and anything filed with a structured
 *  hand-off — and HTML collapses those newlines into one running paragraph.
 *  A teammate opening an urgent ticket has about thirty seconds; give them
 *  the line breaks the author wrote. */
function InternalNotesCard({ inquiry }) {
  if (!inquiry.summary && !inquiry.recommendation) return null;
  return (
    <section className="rounded-2xl border border-amber-400/15 bg-amber-500/[0.04] p-3.5 space-y-2.5">
      <h3 className="text-[10.5px] uppercase tracking-wider font-semibold text-amber-200/85">
        Stephanie&apos;s notes · internal
      </h3>
      {inquiry.summary ? (
        <div>
          <div className="text-[10px] uppercase tracking-wider font-medium text-white/35 mb-0.5">Summary</div>
          <p className="text-[13px] text-white/85 leading-relaxed whitespace-pre-line">{inquiry.summary}</p>
        </div>
      ) : null}
      {inquiry.recommendation ? (
        <div>
          <div className="text-[10px] uppercase tracking-wider font-medium text-white/35 mb-0.5">Recommendation</div>
          <p className="text-[13px] text-white/85 leading-relaxed whitespace-pre-line">{inquiry.recommendation}</p>
        </div>
      ) : null}
    </section>
  );
}

function ResolutionCard({ inquiry }) {
  if (inquiry.status !== 'resolved' || !inquiry.resolution_note) return null;
  return (
    <section className="rounded-2xl border border-emerald-400/20 bg-emerald-500/[0.06] p-3.5">
      <h3 className="text-[10.5px] uppercase tracking-wider font-semibold text-emerald-300/85 mb-1.5">
        Answer sent to the customer
      </h3>
      <p className="text-[13px] text-white/90 leading-relaxed whitespace-pre-wrap">{inquiry.resolution_note}</p>
      {inquiry.resolved_at ? (
        <p className="text-[10.5px] text-white/35 mt-2">
          Resolved {formatAbsolute(inquiry.resolved_at)} · on their trip page and emailed to them
        </p>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Detail pane — conversation
// ---------------------------------------------------------------------------

/**
 * The linked concierge transcript. Same bubble language as the customer
 * chat and AdminSupportInbox: customer right in brand, specialist left in
 * neutral with their portrait. Consults render inline before the reply
 * they produced; the consult that filed THIS inquiry gets a ring and is
 * scrolled into view by the parent once the thread lands.
 */
/**
 * Opens the real trip page for an inquiry's booking reference.
 *
 * An inquiry stores `booking_reference` and no booking id, so this resolves
 * the reference through POST /api/admin/booking and hands the row to the same
 * FlightDetailsModal that Trips and Risk Review use. 131 of 157 inquiries
 * carry a reference; answering one almost always means looking at the trip,
 * and until now that was a separate page and a manual search (2026-09-18).
 */
function InquiryTripModal({ reference, currentUser, onClose }) {
  const [trip, setTrip] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    FlightBooking.fetchFull(null, { references: [reference] })
      .then((rows) => {
        if (cancelled) return;
        if (!rows || rows.length === 0) { setError(`No booking on file for ${reference}.`); return; }
        setTrip(rows);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[InquiriesDesk] could not load the trip:', err?.message || err);
        setError('That trip could not be loaded.');
      });
    return () => { cancelled = true; };
  }, [reference]);

  if (error) {
    return (
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" onClick={onClose}>
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
        <div className="relative z-10 bg-[#0f0f0f] border border-white/10 rounded-2xl px-5 py-4 max-w-sm" onClick={(e) => e.stopPropagation()}>
          <p className="text-white text-[14px] mb-3">{error}</p>
          <button type="button" onClick={onClose} className="h-8 px-3 rounded-full bg-white/10 hover:bg-white/15 text-white text-[12.5px] transition-colors">
            Back to inquiries
          </button>
        </div>
      </div>
    );
  }
  if (!trip) {
    return (
      <div className="fixed inset-0 z-[70] flex items-center justify-center" onClick={onClose}>
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
        <div className="relative z-10 text-[13px] text-white/70">Opening {reference}…</div>
      </div>
    );
  }
  return (
    <FlightDetailsModal
      trip={trip}
      isOpen
      onClose={onClose}
      onUpdate={onClose}
      currentUser={currentUser}
      backLabel="Back to inquiries"
    />
  );
}

/** No Defera account (migration 185): a guest request always opens with their first message. */
const isGuestRequest = (inq) => Boolean(inq) && !inq.user_id && Array.isArray(inq.guest_thread) && inq.guest_thread.length > 0;

/** A guest (no Defera account, migration 185): their emails with the team, answered by email. */
function GuestThread({ inquiry }) {
  const entries = Array.isArray(inquiry.guest_thread) ? inquiry.guest_thread : [];
  return (
    <div className="px-3 sm:px-4 py-4 space-y-3">
      <div className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-[12px] text-white/70">
        No Defera account ({inquiry.user_email}). {inquiry.source === 'team_forward' ? 'Forwarded by the team. ' : ''}Your answer goes to them by email, and their reply comes back here.
      </div>
      {entries.map((e, i) => (
        <div key={`${e.at || i}-${i}`} className={e.role === 'team' ? 'ml-6' : 'mr-6'}>
          <div className="text-[11px] text-white/45 mb-1">
            {e.role === 'team' ? (e.author || 'Defera team') : 'Customer by email'}
            {e.at ? ` · ${new Date(e.at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}
          </div>
          <div className={`rounded-2xl px-3 py-2 text-[13px] leading-relaxed whitespace-pre-wrap ${e.role === 'team' ? 'bg-brand-600/20 text-white/90' : 'bg-white/[0.06] text-white/85'}`}>
            {e.content}
          </div>
        </div>
      ))}
    </div>
  );
}


function ConversationThread({ threadState, inquiry }) {
  if (isGuestRequest(inquiry)) return <GuestThread inquiry={inquiry} />;
  if (!inquiry.thread_id && !threadState.loading && !threadState.data) {
    return (
      <div className="px-4 py-10 text-center text-[13px] text-white/50">
        No linked chat — this inquiry was filed from the{' '}
        {(CATEGORY_LABELS[inquiry.category] || 'service').toLowerCase().replace(/ request$| question$/, '')} flow,
        and this customer has no conversations with the desk.
      </div>
    );
  }
  if (threadState.loading) {
    return <div className="px-4 py-10 text-center text-[13px] text-white/50">Loading conversation…</div>;
  }
  if (threadState.error) {
    return <div className="px-4 py-6 text-[12.5px] text-rose-300">{threadState.error}</div>;
  }
  const data = threadState.data;
  if (!data) return null;
  if (!data.messages.length) {
    return <div className="px-4 py-10 text-center text-[13px] text-white/50">No messages in this thread.</div>;
  }

  return (
    <div className="px-3 sm:px-4 py-4 space-y-3">
      {data.unlinked && (
        <div className="rounded-xl border border-amber-400/20 bg-amber-500/[0.07] px-3 py-2 text-[12px] text-amber-100/85">
          This inquiry wasn&apos;t filed from a conversation. Showing this customer&apos;s most
          recent chat with the desk for context — it may be about something else.
        </div>
      )}
      {data.messages.map((m) => {
        const isUser = m.role === 'user';
        // A message from the team, posted in the customer's chat.
        const isTeam = m.role === 'team';
        const spec = isUser || isTeam ? null : getSpecialist(m.specialist_id);
        const consultsHere = (data.consults || []).filter((c) => c.assistant_message_id === m.id);
        return (
          <div key={m.id}>
            {consultsHere.map((c) => {
              const filedThis = c.id === inquiry.consult_id;
              return (
                <div
                  // filedThis participates in the key so switching between two
                  // inquiries on the SAME thread remounts the consult and its
                  // defaultOpen tracks the newly filed one (audit F6).
                  key={`${c.id}${filedThis ? ':filed' : ''}`}
                  id={filedThis ? 'filed-consult' : undefined}
                  // scroll-mt clears the sticky identity header (mobile) /
                  // pane top (lg) when the auto-scroll lands here.
                  className={filedThis ? 'rounded-2xl ring-1 ring-amber-400/40 scroll-mt-24 lg:scroll-mt-3' : undefined}
                >
                  {filedThis ? (
                    <div className="text-[10px] uppercase tracking-wider font-medium text-amber-200/90 px-3 pt-2">
                      This consult filed the inquiry
                    </div>
                  ) : null}
                  <ConsultTranscript consult={c} defaultOpen={filedThis} />
                </div>
              );
            })}
            {/* No portraits here by design (founder call, 2026-08-03) — the
                name caption on each bubble is the whole identity layer. */}
            <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[88%] sm:max-w-[78%] px-3.5 py-2.5 rounded-[18px] text-[14.5px] ${
                  isUser
                    ? 'bg-brand-600/85 text-white rounded-br-[5px]'
                    : isTeam
                      ? 'bg-emerald-500/[0.08] text-white/95 rounded-bl-[5px] border border-emerald-400/25'
                      : 'bg-white/[0.06] text-white/95 rounded-bl-[5px] border border-white/[0.05]'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-[10px] uppercase tracking-wider font-medium ${isUser ? 'text-white/70' : isTeam ? 'text-emerald-200/85' : 'text-brand-300/85'}`}>
                    {isUser ? customerName(inquiry) : isTeam ? (m.author_label || 'Defera team') : spec.name}
                  </span>
                  <span className={`ml-auto text-[10px] ${isUser ? 'text-white/50' : 'text-white/35'}`}>
                    {formatAbsolute(m.created_at)}
                  </span>
                </div>
                <PreviewMarkdown text={m.content} />
              </div>
            </div>
          </div>
        );
      })}

      {/* Consults that never got pinned to a persisted reply — rare, but
          they're still part of this customer's record. */}
      {(data.consults || [])
        .filter((c) => !c.assistant_message_id || !data.messages.some((m) => m.id === c.assistant_message_id))
        .map((c) => (
          <div key={c.id} id={c.id === inquiry.consult_id ? 'filed-consult' : undefined}>
            <div className="text-[10.5px] uppercase tracking-wider font-semibold text-white/35 mb-1 px-1">
              Unlinked consult
            </div>
            <ConsultTranscript consult={c} defaultOpen={c.id === inquiry.consult_id} />
          </div>
        ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const STATUS_FILTERS = [
  { id: 'outstanding', label: 'Outstanding' },
  { id: 'replied', label: 'Replied' },
  { id: 'overdue', label: 'Overdue' },
  { id: 'resolved', label: 'Resolved' },
  { id: 'dismissed', label: 'Dismissed' },
  { id: 'all', label: 'All' },
];

/** lg and up, where the conversation pane scrolls on its own (below it, the page does). */
const isWideScreen = () => typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(min-width: 1024px)').matches;

export default function InquiriesDesk() {
  const location = useLocation();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);

  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState('outstanding');
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [query, setQuery] = useState('');

  const [selectedId, setSelectedId] = useState(
    () => new URLSearchParams(location.search).get('id') || null,
  );
  const [threadState, setThreadState] = useState({ loading: false, error: null, data: null });
  /** Booking reference whose trip page is open over the desk, if any. */
  const [tripRef, setTripRef] = useState(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [actionError, setActionError] = useState(null);
  /** What happened to the last "Send, keep open" (in the chat, emailed or not). */
  const [replyNotice, setReplyNotice] = useState(null);
  const [saving, setSaving] = useState(false);
  /** Bumped after a send so the conversation reloads with the team's message in it. */
  const [threadNonce, setThreadNonce] = useState(0);
  /** Background re-reads of the open conversation (TRANSCRIPT_REFRESH_MS). */
  const [threadTick, setThreadTick] = useState(0);
  /** Stephanie drafting the team's next message: { id, loading, error } for the open request. */
  const [draftState, setDraftState] = useState({ id: null, loading: false, error: null });
  /** Requests already drafted (or being drafted) this session, keyed by request and reply time. */
  const draftRequestedRef = useRef(new Set());
  const [claiming, setClaiming] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [scheduleError, setScheduleError] = useState(null);
  // Silent duplicate-erase: two-tap armed so a stray click can't vanish a
  // real inquiry. Arm state resets whenever the selection changes.
  const [dismissArmed, setDismissArmed] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const dismissArmedAtRef = useRef(0);
  /** The inquiry just dismissed, while its Undo bar is up: { inquiry, error? }. */
  const [undoDismiss, setUndoDismiss] = useState(null);
  const [undoing, setUndoing] = useState(false);
  const [restoring, setRestoring] = useState(false);

  // Thread transcripts don't change while the desk is open — cache them so
  // flipping between inquiries on the same thread doesn't refetch.
  const threadCache = useRef(new Map());
  // Per-inquiry composer drafts: tapping another row to check something must
  // not wipe a half-written customer answer (audit F4, 2026-08-04).
  const noteDraftsRef = useRef(new Map());
  const listFetchToken = useRef(0);
  /** Foreground list loads in flight (the background re-read waits for them). */
  const foregroundLoadsRef = useRef(0);
  const selectedIdRef = useRef(null);
  const conversationRef = useRef(null);
  // The id the page was opened with (email deep link). If the default
  // "outstanding" list doesn't contain it — it may have been resolved since
  // the email was sent — widen to "All" once so the link still lands.
  const deepLinkId = useRef(new URLSearchParams(location.search).get('id') || null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await UserEntity.me();
        if (!cancelled) setUser(me || null);
      } catch {
        /* signed out */
      } finally {
        if (!cancelled) setAuthReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const buildAuthHeader = useCallback(async () => {
    try {
      const { data } = await supabase.auth.getSession();
      const tok = data?.session?.access_token;
      return tok ? `Bearer ${tok}` : null;
    } catch {
      return null;
    }
  }, []);

  const admin = isCurrentUserAdmin(user);

  const loadList = useCallback(async (status = statusFilter, { background = false } = {}) => {
    // A background re-read never cuts in on a load the admin is waiting on:
    // superseding it would leave "Loading inquiries…" up for good.
    if (background && foregroundLoadsRef.current > 0) return;
    const token = listFetchToken.current + 1;
    listFetchToken.current = token;
    if (!background) {
      foregroundLoadsRef.current += 1;
      setListLoading(true);
      setListError(null);
    }
    try {
      const authHeader = await buildAuthHeader();
      if (!authHeader) throw new Error('Session expired — please sign back in.');
      const res = await fetch(
        `/api/admin/concierge-inquiries?status=${encodeURIComponent(status)}&limit=200`,
        // Never the browser's copy: a re-read must show what came in since.
        { headers: { Authorization: authHeader }, cache: 'no-store' },
      );
      if (token !== listFetchToken.current) return;
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) throw new Error(data?.error || `Request failed (${res.status}).`);
      const fresh = data.inquiries || [];
      // A background refresh never takes away the request being worked: a
      // just-resolved row leaves the "outstanding" list, but the admin is
      // still looking at it (same reason resolve doesn't reload).
      setInquiries((prev) => {
        if (!background) return fresh;
        const open = prev.find((i) => i.id === selectedIdRef.current);
        return open && !fresh.some((i) => i.id === open.id) ? [...fresh, open] : fresh;
      });
      setStats(data.stats || null);
    } catch (err) {
      if (token !== listFetchToken.current) return;
      if (!background) setListError(err?.message || 'Failed to load inquiries.');
    } finally {
      if (!background) foregroundLoadsRef.current -= 1;
      if (token === listFetchToken.current && !background) setListLoading(false);
    }
  }, [buildAuthHeader, statusFilter]);

  useEffect(() => {
    if (!authReady || !admin) return;
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authReady, admin]);

  const applyFilter = useCallback((next) => {
    setStatusFilter(next);
    loadList(next);
  }, [loadList]);

  // Client-side quick filter over the loaded page — the list endpoint has
  // no search param, and 200 rows is comfortably filterable in memory.
  const visibleInquiries = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return inquiries;
    return inquiries.filter((inq) => [
      inq.user_first_name,
      inq.user_email,
      inq.booking_reference,
      inq.title,
      inq.customer_question,
      inq.category,
    ].some((v) => v && String(v).toLowerCase().includes(q)));
  }, [inquiries, query]);

  const selected = useMemo(
    () => inquiries.find((i) => i.id === selectedId) || null,
    [inquiries, selectedId],
  );
  // A schedule error belongs to the request it was about.
  useEffect(() => { setScheduleError(null); }, [selectedId]);

  useEffect(() => {
    if (listLoading || !deepLinkId.current) return;
    // Found (or superseded): consume the ref so the widen can never fire on
    // a LATER refresh that happens to drop the row (audit F5).
    if (selected || selectedId !== deepLinkId.current) { deepLinkId.current = null; return; }
    deepLinkId.current = null; // one retry, ever
    if (statusFilter === 'outstanding') applyFilter('all');
  }, [listLoading, selected, selectedId, statusFilter, applyFilter]);

  // Selection is URL-mirrored (?id=) so an inquiry is deep-linkable from
  // the notification email. Same replace-navigation pattern as Staff.
  const select = useCallback((id) => {
    setSelectedId(id);
    setActionError(null);
    setReplyNotice(null);
    const next = new URLSearchParams(location.search);
    if (id) next.set('id', id);
    else next.delete('id');
    const nextSearch = next.toString();
    if (nextSearch !== location.search.replace(/^\?/, '')) {
      navigate({ pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' }, { replace: true });
    }
    if (id && typeof window !== 'undefined' && window.innerWidth < 1024) window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  // New selection → the composer holds only an unsent draft for this request.
  // Never the answer already sent (resolution_note): it showed up ready to
  // send again in the box (founder, 2026-10-25); the "Answer sent" card and
  // the conversation already show it.
  useEffect(() => {
    selectedIdRef.current = selected?.id || null;
    setNoteDraft(noteDraftsRef.current.get(selected?.id) ?? '');
    setDismissArmed(false);
  }, [selected?.id]);

  // Load the linked transcript: from cache at once when we have it, then
  // re-read, on selection and every TRANSCRIPT_REFRESH_MS (threadTick).
  const transcriptForRef = useRef(null);
  useEffect(() => {
    const refreshOnly = transcriptForRef.current === selected?.id;
    transcriptForRef.current = selected?.id || null;
    if (!selected?.thread_id) {
      // The fallback conversation is re-read too: half of all requests have
      // no linked chat, and their customers keep talking.
      // Half of all inquiries carry no thread_id — they were filed by a flow
      // rather than a conversation (gift-card widget, automatic confirmation
      // release). 53 of those 75 belong to customers who HAVE talked to the
      // desk, and the page used to say "no linked chat" and show nothing,
      // hiding the very context needed to answer them (2026-09-18). Fall back
      // to the customer's own most recent conversation, clearly labelled as
      // not being the one that filed this.
      // A guest has no chat to look up: their emails are on the request.
      if (!selected?.user_email || isGuestRequest(selected)) {
        setThreadState({ loading: false, error: null, data: null });
        return undefined;
      }
      let cancelledFallback = false;
      if (!refreshOnly) setThreadState({ loading: true, error: null, data: null });
      (async () => {
        try {
          const authHeader = await buildAuthHeader();
          if (!authHeader) throw new Error('Session expired — please sign back in.');
          const listRes = await fetch(
            `/api/admin/support-threads?search=${encodeURIComponent(selected.user_email)}&limit=10`,
            { headers: { Authorization: authHeader }, cache: 'no-store' },
          );
          const list = await listRes.json().catch(() => ({}));
          const mine = (list?.threads || []).filter((t) => t.user_email === selected.user_email);
          if (!mine.length) {
            if (!cancelledFallback && !refreshOnly) setThreadState({ loading: false, error: null, data: null });
            return;
          }
          const best = mine[0]; // the list is ordered by last_message_at desc
          const res = await fetch(`/api/admin/support-threads/${encodeURIComponent(best.id)}?inquiryId=${encodeURIComponent(selected.id)}`, {
            headers: { Authorization: authHeader },
            cache: 'no-store',
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok || !data?.ok) throw new Error(data?.error || 'Could not load that conversation.');
          const payload = {
            thread: data.thread,
            messages: data.messages || [],
            consults: data.consults || [],
            unlinked: true,
          };
          if (!cancelledFallback) {
            setThreadState((prev) => (sameTranscript(prev.data, payload) ? prev : { loading: false, error: null, data: payload }));
          }
        } catch (err) {
          // A failed background re-read keeps what's on screen.
          if (!cancelledFallback && !refreshOnly) setThreadState({ loading: false, error: err?.message || 'Could not load the customer\'s conversations.', data: null });
        }
      })();
      return () => { cancelledFallback = true; };
    }
    // Keyed by request too: the transcript includes the team's messages
    // about THIS request, even ones that landed in a newer conversation.
    const cacheKey = `${selected.thread_id}:${selected.id}`;
    const cached = threadCache.current.get(cacheKey);
    if (!refreshOnly) {
      setThreadState(cached ? { loading: false, error: null, data: cached } : { loading: true, error: null, data: null });
    }
    let cancelled = false;
    (async () => {
      try {
        const authHeader = await buildAuthHeader();
        if (!authHeader) throw new Error('Session expired — please sign back in.');
        const res = await fetch(`/api/admin/support-threads/${encodeURIComponent(selected.thread_id)}?inquiryId=${encodeURIComponent(selected.id)}`, {
          headers: { Authorization: authHeader },
          cache: 'no-store',
        });
        const data = await res.json().catch(() => ({}));
        if (res.status === 404) throw new Error('That chat thread no longer exists.');
        if (!res.ok || !data?.ok) throw new Error(data?.error || `Failed to load the conversation (${res.status}).`);
        const payload = { thread: data.thread, messages: data.messages || [], consults: data.consults || [] };
        threadCache.current.set(cacheKey, payload);
        if (!cancelled) {
          setThreadState((prev) => (sameTranscript(prev.data, payload) ? prev : { loading: false, error: null, data: payload }));
        }
      } catch (err) {
        // A failed background re-read keeps what's on screen.
        if (!cancelled && !(refreshOnly || cached)) {
          setThreadState({ loading: false, error: err?.message || 'Failed to load the conversation.', data: null });
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id, selected?.thread_id, threadNonce, threadTick]);

  // Background re-reads while the tab is visible: the open conversation
  // every TRANSCRIPT_REFRESH_MS, the queue every LIST_REFRESH_MS, and both
  // at once when the tab comes back into view.
  const loadListRef = useRef(loadList);
  loadListRef.current = loadList;
  // The conversation on screen (its linked chat, or the fallback one shown).
  const shownThreadRef = useRef(null);
  shownThreadRef.current = threadState.data?.thread?.id || selected?.thread_id || null;

  // Live (migration 203): the moment a message lands or a request changes,
  // the database signals the desk and it re-reads at once: the open
  // conversation when the message is in it, and the queue. The timed
  // re-reads below stay as the fallback.
  useEffect(() => {
    if (!authReady || !admin) return undefined;
    let queueTimer = null;
    const stop = listenForSignals('desk', (payload, event) => {
      if (event === 'message' && payload.thread_id && payload.thread_id === shownThreadRef.current) {
        setThreadTick((n) => n + 1);
      }
      // One queue re-read for a burst of signals.
      clearTimeout(queueTimer);
      queueTimer = setTimeout(() => loadListRef.current(undefined, { background: true }), 400);
    });
    return () => {
      clearTimeout(queueTimer);
      stop();
    };
  }, [authReady, admin]);
  useEffect(() => {
    if (!authReady || !admin) return undefined;
    const visible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden';
    const transcriptTimer = setInterval(() => { if (visible()) setThreadTick((n) => n + 1); }, TRANSCRIPT_REFRESH_MS);
    const listTimer = setInterval(() => { if (visible()) loadListRef.current(undefined, { background: true }); }, LIST_REFRESH_MS);
    const onVisible = () => {
      if (!visible()) return;
      setThreadTick((n) => n + 1);
      loadListRef.current(undefined, { background: true });
    };
    document.addEventListener('visibilitychange', onVisible);
    // Coming back to the window, to a restored page (phones, the iOS app) or
    // online again re-reads at once too, not only a tab switch.
    window.addEventListener('focus', onVisible);
    window.addEventListener('pageshow', onVisible);
    window.addEventListener('online', onVisible);
    return () => {
      clearInterval(transcriptTimer);
      clearInterval(listTimer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      window.removeEventListener('pageshow', onVisible);
      window.removeEventListener('online', onVisible);
    };
  }, [authReady, admin]);

  // Once the transcript is in, open it at the newest message (founder,
  // 2026-10-22): the customer's latest reply or the team's last message is
  // what gets worked. The consult that filed the inquiry keeps its ring for
  // anyone scrolling up to it. On a wide screen the conversation pane
  // scrolls; on a phone the page does. Instant, not smooth: a smooth scroll
  // gets cut short by the renders that land right after the thread data.
  const pinnedToEndRef = useRef(true);
  const scrollConversationToEnd = useCallback(() => {
    const pane = conversationRef.current;
    if (pane && isWideScreen()) {
      pane.scrollTop = pane.scrollHeight;
    } else {
      window.scrollTo({ top: document.documentElement.scrollHeight });
    }
  }, []);
  // A newly opened request lands on its newest message; a background
  // re-read that brings a new message follows it only while the admin is
  // at the bottom, never pulling them away from what they scrolled up to.
  const scrolledForRef = useRef(null);
  useEffect(() => {
    if (!threadState.data) return undefined;
    const opened = scrolledForRef.current !== selected?.id;
    scrolledForRef.current = selected?.id || null;
    if (!opened && !pinnedToEndRef.current) return undefined;
    pinnedToEndRef.current = true;
    const frame = requestAnimationFrame(scrollConversationToEnd);
    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadState.data, scrollConversationToEnd]);

  // Stay at the newest message while the pane changes size under it
  // (Stephanie's draft card arriving above the composer shrinks it), unless
  // the admin has scrolled up to read.
  useEffect(() => {
    const pane = conversationRef.current;
    if (!pane || typeof ResizeObserver === 'undefined') return undefined;
    const onScroll = () => {
      pinnedToEndRef.current = pane.scrollHeight - pane.scrollTop - pane.clientHeight < 48;
    };
    const observer = new ResizeObserver(() => {
      if (pinnedToEndRef.current && isWideScreen()) pane.scrollTop = pane.scrollHeight;
    });
    pane.addEventListener('scroll', onScroll, { passive: true });
    observer.observe(pane);
    return () => {
      pane.removeEventListener('scroll', onScroll);
      observer.disconnect();
    };
  }, [selected?.id]);

  const claim = useCallback(async () => {
    if (!selected) return;
    setClaiming(true);
    setActionError(null);
    try {
      const authHeader = await buildAuthHeader();
      if (!authHeader) throw new Error('Session expired — please sign back in.');
      const res = await fetch(`/api/admin/concierge-inquiries/${encodeURIComponent(selected.id)}/claim`, {
        method: 'POST',
        headers: { Authorization: authHeader },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) throw new Error(data?.error || `Request failed (${res.status}).`);
      setInquiries((prev) => prev.map((i) => (i.id === selected.id ? { ...i, status: 'in_progress' } : i)));
      setStats((prev) => (prev ? {
        ...prev,
        open: Math.max(0, prev.open - 1),
        in_progress: prev.in_progress + 1,
      } : prev));
    } catch (err) {
      setActionError(err?.message || 'Failed to claim that inquiry.');
    } finally {
      setClaiming(false);
    }
  }, [buildAuthHeader, selected]);

  // Schedule (or clear) the day this request is promised for. Admins only;
  // the server enforces it too. The customer's card and the concierge follow
  // the new date; due_by comes back moved to that day.
  const scheduleRequest = useCallback(async (date) => {
    if (!selected) return;
    setScheduling(true);
    setScheduleError(null);
    try {
      const authHeader = await buildAuthHeader();
      if (!authHeader) throw new Error('Session expired — please sign back in.');
      const res = await fetch(`/api/admin/concierge-inquiries/${encodeURIComponent(selected.id)}/schedule`, {
        method: 'POST',
        headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ scheduledFor: date || null }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) throw new Error(data?.error || `Request failed (${res.status}).`);
      setInquiries((prev) => prev.map((i) => (i.id === selected.id
        ? { ...i, scheduled_for: data.inquiry?.scheduled_for ?? null, due_by: data.inquiry?.due_by ?? i.due_by }
        : i)));
    } catch (err) {
      setScheduleError(err?.message || 'Failed to schedule that request.');
    } finally {
      setScheduling(false);
    }
  }, [buildAuthHeader, selected]);

  // Erase a duplicate/misfiled inquiry. SILENT: the endpoint never emails
  // the customer; the dupe card just disappears from their trip page. The
  // row leaves the desk list immediately — there is nothing left to work.
  const dismissAsDuplicate = useCallback(async () => {
    if (!selected) return;
    if (!dismissArmed) {
      dismissArmedAtRef.current = Date.now();
      setDismissArmed(true);
      return;
    }
    // The second half of a double tap: stay armed, change nothing.
    if (!isDeliberateConfirm(dismissArmedAtRef.current, Date.now())) return;
    const dismissedInquiry = selected;
    setDismissing(true);
    setActionError(null);
    try {
      const authHeader = await buildAuthHeader();
      if (!authHeader) throw new Error('Session expired — please sign back in.');
      const res = await fetch(`/api/admin/concierge-inquiries/${encodeURIComponent(selected.id)}/dismiss`, {
        method: 'POST',
        headers: { Authorization: authHeader },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) throw new Error(data?.error || `Request failed (${res.status}).`);
      const wasOverdue = isOverdue(selected);
      noteDraftsRef.current.delete(selected.id);
      setInquiries((prev) => prev.filter((i) => i.id !== selected.id));
      setStats((prev) => (prev ? {
        ...prev,
        open: Math.max(0, prev.open - (selected.status === 'open' ? 1 : 0)),
        in_progress: Math.max(0, prev.in_progress - (selected.status === 'in_progress' ? 1 : 0)),
        outstanding: Math.max(0, prev.outstanding - 1),
        overdue: Math.max(0, prev.overdue - (wasOverdue ? 1 : 0)),
        dismissed: (prev.dismissed || 0) + 1,
      } : prev));
      select(null);
      setUndoDismiss({ inquiry: dismissedInquiry });
    } catch (err) {
      setActionError(err?.message || 'Failed to dismiss that inquiry.');
    } finally {
      setDismissing(false);
      setDismissArmed(false);
    }
  }, [buildAuthHeader, selected, dismissArmed, select]);

  // Put an admin-dismissed inquiry back: on the worklist and on the
  // customer's trip page. Silent, like the dismiss. Returns { ok, error }.
  const restoreInquiry = useCallback(async (inquiry, toStatus = 'open') => {
    try {
      const authHeader = await buildAuthHeader();
      if (!authHeader) throw new Error('Session expired — please sign back in.');
      const res = await fetch(`/api/admin/concierge-inquiries/${encodeURIComponent(inquiry.id)}/restore`, {
        method: 'POST',
        headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: toStatus }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) throw new Error(data?.error || `Request failed (${res.status}).`);
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err?.message || 'Failed to restore that inquiry.' };
    }
  }, [buildAuthHeader]);

  useEffect(() => {
    if (!undoDismiss || undoDismiss.error) return undefined;
    const timer = setTimeout(() => setUndoDismiss(null), UNDO_WINDOW_MS);
    return () => clearTimeout(timer);
  }, [undoDismiss]);

  const undoLastDismiss = useCallback(async () => {
    if (!undoDismiss || undoing) return;
    const { inquiry } = undoDismiss;
    setUndoing(true);
    // Back to exactly where it was: open, or still claimed if it was.
    const out = await restoreInquiry(inquiry, inquiry.status);
    setUndoing(false);
    if (!out.ok) {
      setUndoDismiss({ inquiry, error: out.error });
      return;
    }
    setUndoDismiss(null);
    await loadList();
    select(inquiry.id);
  }, [undoDismiss, undoing, restoreInquiry, loadList, select]);

  const restoreSelected = useCallback(async () => {
    if (!selected || restoring) return;
    const id = selected.id;
    setRestoring(true);
    setActionError(null);
    const out = await restoreInquiry(selected, 'open');
    setRestoring(false);
    if (!out.ok) {
      setActionError(out.error);
      return;
    }
    // It's outstanding again — follow it there.
    applyFilter('outstanding');
    select(id);
  }, [selected, restoring, restoreInquiry, applyFilter, select]);

  /** The server erased the account, resolved this request and scrubbed its text. */
  const accountDeleted = useCallback((id) => {
    setInquiries((prev) => prev.map((i) => (i.id === id ? {
      ...i, status: 'resolved', resolution_note: 'Account deleted.', resolved_at: new Date().toISOString(), user_email: null, user_first_name: null,
    } : i)));
    setStats((prev) => (prev ? { ...prev, outstanding: Math.max(0, prev.outstanding - 1), resolved: prev.resolved + 1 } : prev));
  }, []);

  const resolve = useCallback(async () => {
    const note = noteDraft.trim();
    // The server rejects an empty note too — resolving with nothing to say
    // would flip the customer's card to "Answered" and show them a blank.
    if (!note || !selected) return;
    setSaving(true);
    setActionError(null);
    try {
      const authHeader = await buildAuthHeader();
      if (!authHeader) throw new Error('Session expired — please sign back in.');
      const res = await fetch(`/api/admin/concierge-inquiries/${encodeURIComponent(selected.id)}/resolve`, {
        method: 'POST',
        headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ resolutionNote: note }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) throw new Error(data?.error || `Request failed (${res.status}).`);
      // Merge (not replace): the resolve payload is a slim column set and
      // must not wipe thread_id/summary off the row we're rendering.
      // Deliberately NOT reloading the list: under the default
      // "outstanding" filter a reload would drop the just-resolved row —
      // and the confirmation the admin is looking at — off the screen.
      // The row stays until they change filters or refresh; the counters
      // are adjusted locally instead.
      const wasOverdue = isOverdue(selected);
      noteDraftsRef.current.delete(selected.id);
      setInquiries((prev) => prev.map((i) => (i.id === selected.id ? {
        ...i, ...(data.inquiry || {}), thread_id: data.inquiry?.thread_id || i.thread_id, ...SPENT_REPLY_AND_DRAFT,
      } : i)));
      // The answer is in their chat now; show it.
      threadCache.current.delete(`${selected.thread_id}:${selected.id}`);
      setThreadNonce((n) => n + 1);
      setStats((prev) => (prev ? {
        ...prev,
        open: Math.max(0, prev.open - (selected.status === 'open' ? 1 : 0)),
        in_progress: Math.max(0, prev.in_progress - (selected.status === 'in_progress' ? 1 : 0)),
        outstanding: Math.max(0, prev.outstanding - 1),
        overdue: Math.max(0, prev.overdue - (wasOverdue ? 1 : 0)),
        resolved: prev.resolved + 1,
        replied: Math.max(0, (prev.replied || 0) - (selected.customer_replied_at ? 1 : 0)),
      } : prev));
    } catch (err) {
      setActionError(err?.message || 'Failed to resolve that inquiry.');
    } finally {
      setSaving(false);
    }
  }, [buildAuthHeader, noteDraft, selected]);

  /** Write to the customer without resolving: a question back, an update. */
  const reply = useCallback(async () => {
    const message = noteDraft.trim();
    if (!message || !selected) return;
    setSaving(true);
    setActionError(null);
    setReplyNotice(null);
    try {
      const authHeader = await buildAuthHeader();
      if (!authHeader) throw new Error('Session expired — please sign back in.');
      const res = await fetch(`/api/admin/concierge-inquiries/${encodeURIComponent(selected.id)}/reply`, {
        method: 'POST',
        headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ message }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) throw new Error(data?.error || `Request failed (${res.status}).`);
      noteDraftsRef.current.delete(selected.id);
      setNoteDraft('');
      setReplyNotice(
        data.duplicate
          ? 'That exact message is already in their chat. Nothing new was sent.'
          : data.emailed
            ? `Sent: it's in their chat${data.pushed ? ', on their iPhone,' : ''} and in their email.`
            : 'Posted in their chat, but the email did not go out (no address on file, or email is off).',
      );
      setInquiries((prev) => prev.map((i) => (i.id === selected.id ? {
        ...i, thread_id: i.thread_id || data.threadId || null, ...(data.duplicate ? {} : SPENT_REPLY_AND_DRAFT),
      } : i)));
      if (!data.duplicate && selected.customer_replied_at) {
        const wasOverdue = isOverdue(selected);
        const stillOpen = ['open', 'in_progress'].includes(selected.status);
        setStats((prev) => (prev ? {
          ...prev,
          replied: Math.max(0, (prev.replied || 0) - 1),
          // A resolved request was outstanding only because they replied.
          outstanding: Math.max(0, prev.outstanding - (stillOpen ? 0 : 1)),
          overdue: Math.max(0, prev.overdue - (wasOverdue && !isOverdue({ ...selected, customer_replied_at: null }) ? 1 : 0)),
        } : prev));
      }
      threadCache.current.delete(`${selected.thread_id}:${selected.id}`);
      setThreadNonce((n) => n + 1);
    } catch (err) {
      setActionError(err?.message || 'Failed to send that message.');
    } finally {
      setSaving(false);
    }
  }, [buildAuthHeader, noteDraft, selected]);

  /** "No reply needed": they answered the team, the team read it, nothing to send. */
  const clearReply = useCallback(async () => {
    if (!selected?.customer_replied_at) return;
    setSaving(true);
    setActionError(null);
    try {
      const authHeader = await buildAuthHeader();
      if (!authHeader) throw new Error('Session expired — please sign back in.');
      const res = await fetch(`/api/admin/concierge-inquiries/${encodeURIComponent(selected.id)}/clear-reply`, {
        method: 'POST',
        headers: { Authorization: authHeader },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) throw new Error(data?.error || `Request failed (${res.status}).`);
      const wasOverdue = isOverdue(selected);
      const stillOpen = ['open', 'in_progress'].includes(selected.status);
      setInquiries((prev) => prev.map((i) => (i.id === selected.id ? { ...i, customer_replied_at: null, customer_reply_preview: null } : i)));
      setStats((prev) => (prev ? {
        ...prev,
        replied: Math.max(0, (prev.replied || 0) - 1),
        outstanding: Math.max(0, prev.outstanding - (stillOpen ? 0 : 1)),
        overdue: Math.max(0, prev.overdue - (wasOverdue && !isOverdue({ ...selected, customer_replied_at: null }) ? 1 : 0)),
      } : prev));
    } catch (err) {
      setActionError(err?.message || 'Failed to update that request.');
    } finally {
      setSaving(false);
    }
  }, [buildAuthHeader, selected]);

  /** Ask Stephanie for a draft of the team's next message (saved on the request). */
  const requestDraft = useCallback(async (inquiry, { force = false } = {}) => {
    if (!inquiry?.id) return;
    const key = `${inquiry.id}:${inquiry.customer_replied_at || ''}`;
    if (!force && draftRequestedRef.current.has(key)) return;
    draftRequestedRef.current.add(key);
    setDraftState({ id: inquiry.id, loading: true, error: null });
    try {
      const authHeader = await buildAuthHeader();
      if (!authHeader) throw new Error('Session expired — please sign back in.');
      // The server serves the saved draft while it stands; force (Redraft)
      // writes a new one.
      const res = await fetch(`/api/admin/concierge-inquiries/${encodeURIComponent(inquiry.id)}/draft`, {
        method: 'POST',
        headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ force }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) throw new Error(data?.error || `Request failed (${res.status}).`);
      setInquiries((prev) => prev.map((i) => (i.id === inquiry.id ? {
        ...i,
        stephanie_draft: data.draft,
        stephanie_draft_notes: data.notes || null,
        stephanie_draft_ready: Boolean(data.ready),
        stephanie_draft_at: data.draftedAt,
      } : i)));
      setDraftState({ id: inquiry.id, loading: false, error: null });
    } catch (err) {
      setDraftState({ id: inquiry.id, loading: false, error: err?.message || 'Stephanie couldn\'t draft this one.' });
    }
  }, [buildAuthHeader]);

  // The team spoke last and the customer hasn't answered: the next move is
  // theirs, so there's nothing for Stephanie to draft (3FW6NF, 10-22: her
  // draft repeated the question the team had just asked).
  const conversation = threadState.data?.messages || [];
  const waitingOnCustomer = Boolean(selected) && !selected.customer_replied_at
    && conversation.length > 0 && conversation[conversation.length - 1].role === 'team';

  // Stephanie drafts only when asked (founder, 2026-10-28: "only to draft
  // when I click for her to do so"). Her card shows for the requests the
  // admin asked her about in this visit; a saved draft from before stays
  // hidden until asked again.
  const [draftsAsked, setDraftsAsked] = useState(() => new Set());
  const askStephanie = useCallback((inquiry, { force = false } = {}) => {
    if (!inquiry?.id) return;
    setDraftsAsked((prev) => (prev.has(inquiry.id) ? prev : new Set(prev).add(inquiry.id)));
    requestDraft(inquiry, { force });
  }, [requestDraft]);

  // --- Gates (mirror Staff.jsx) --------------------------------------------

  if (authReady && !user) {
    return (
      <div className="min-h-[60vh] bg-black text-white">
        <div className="max-w-3xl mx-auto px-4 py-10 text-center">
          <h1 className="text-2xl font-semibold mb-3">Inquiries</h1>
          <p className="text-sm text-white/60 mb-4">Sign in to continue.</p>
          <Button asChild size="sm" className="bg-white text-black hover:bg-white/90">
            <Link to="/home?auth_mode=login">Sign in</Link>
          </Button>
        </div>
      </div>
    );
  }

  if (authReady && user && !admin) {
    return (
      <div className="min-h-[60vh] bg-black text-white">
        <div className="max-w-3xl mx-auto px-4 py-10">
          <h1 className="text-2xl font-semibold mb-4">Inquiries</h1>
          <div className="rounded-2xl border border-red-400/30 bg-white/[0.02] p-4 flex items-start gap-2.5">
            <ShieldAlert className="w-4 h-4 text-red-300 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-white mb-0.5">This page is restricted</p>
              <p className="text-white/60 text-xs">
                The inquiries desk is an internal admin tool. If you think you should have access, contact support.
              </p>
              <div className="mt-3">
                <Button asChild size="sm" variant="outline" className="h-7 text-xs text-white border-white/20 bg-transparent hover:bg-white/10">
                  <Link to="/dashboard">Back to Trips</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Keyed on the RESOLVED selection, not the raw ?id=: a deep link whose row
  // hasn't loaded (or never loads — list error, id beyond the 200-row page)
  // must keep the list + its loading/error states on screen on mobile, not
  // hide every pane and render a black void (audit F1, 2026-08-04).
  const detailOpenMobile = Boolean(selected);
  // Open requests, and resolved ones the customer has answered since.
  const showComposer = needsAnswer(selected);
  const stillOpen = ['open', 'in_progress'].includes(selected?.status);
  const draftLoading = draftState.loading && draftState.id === selected?.id;
  const draftError = !draftState.loading && draftState.id === selected?.id ? draftState.error : null;
  // Her draft, sent exactly as written, goes out under her name (server decides the same way).
  const sendsAsStephanie = Boolean(selected?.stephanie_draft) && sameWords(noteDraft, selected.stephanie_draft);

  return (
    <>
      <Helmet>
        <title>Inquiries — Defera Admin</title>
      </Helmet>

      {/* lg+: the desk fits the viewport exactly (no min-h — that would add
          80px of dead window scroll under the fixed nav) and each pane
          scrolls internally — an app, not a page. Below lg the page keeps
          min-h and scrolls naturally so the URL bar can collapse. */}
      <div className="min-h-[100dvh] lg:min-h-0 bg-black text-white">
        <div className="max-w-[1440px] mx-auto px-3 sm:px-6 pt-2 sm:pt-4 pb-6 lg:pb-4 flex flex-col lg:h-[calc(100dvh_-_var(--app-header-height))]">
          {/* Header — hidden on mobile while a detail is open so the
              conversation gets the full viewport. */}
          <header className={`${detailOpenMobile ? 'hidden lg:flex' : 'flex'} items-center justify-between gap-3 mb-3 sm:mb-4 flex-shrink-0`}>
            <div className="min-w-0">
              <Link
                to="/staff"
                className="inline-flex items-center gap-1 text-[11.5px] text-white/45 hover:text-white transition mb-1"
              >
                <ChevronLeft className="w-3.5 h-3.5" strokeWidth={2} />
                Staff
              </Link>
              <div className="flex items-center gap-2.5 min-w-0">
                <h1 className="text-[20px] sm:text-[24px] font-semibold tracking-tight leading-none">Inquiries</h1>
                {stats ? (
                  <p className="text-[11.5px] text-white/55 leading-none pt-0.5 truncate">
                    {stats.outstanding} outstanding
                    {stats.replied ? <span className="text-sky-200"> · {stats.replied} replied</span> : null}
                    {stats.overdue ? <span className="text-red-300"> · {stats.overdue} overdue</span> : null}
                    {' '}· {stats.resolved} resolved
                  </p>
                ) : null}
              </div>
            </div>
            <button
              type="button"
              onClick={() => loadList()}
              className="inline-flex items-center gap-1.5 text-[11.5px] font-medium px-2.5 h-8 rounded-full bg-white/[0.04] border border-white/[0.08] text-white/55 hover:text-white transition flex-shrink-0"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${listLoading ? 'animate-spin' : ''}`} strokeWidth={1.75} />
              Refresh
            </button>
          </header>

          {/* Filters + quick search */}
          <div className={`${detailOpenMobile ? 'hidden lg:flex' : 'flex'} items-center gap-2 mb-3 flex-wrap sm:flex-nowrap flex-shrink-0`}>
            {/* Five tabs can outgrow a 375px phone — scroll sideways there
                rather than push the page wide. */}
            <div className="flex items-center rounded-full bg-white/[0.04] border border-white/[0.08] p-0.5 max-w-full overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:flex-shrink-0" role="group" aria-label="Status filter">
              {STATUS_FILTERS.map((f) => {
                const count = f.id === 'outstanding' ? stats?.outstanding
                  : f.id === 'replied' ? stats?.replied
                  : f.id === 'overdue' ? stats?.overdue
                    : f.id === 'resolved' ? stats?.resolved
                      : f.id === 'dismissed' ? stats?.dismissed
                        : null;
                return (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => applyFilter(f.id)}
                    aria-pressed={statusFilter === f.id}
                    className={`inline-flex items-center gap-1.5 text-[11.5px] font-medium px-2.5 h-8 rounded-full transition flex-shrink-0 whitespace-nowrap ${
                      statusFilter === f.id ? 'bg-white/[0.09] text-white' : 'text-white/60 hover:text-white'
                    }`}
                  >
                    {f.label}
                    {typeof count === 'number' && count > 0 ? (
                      <span className={`text-[10px] tabular-nums ${f.id === 'overdue' ? 'text-red-300' : 'text-white/40'}`}>
                        {count}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
            <div className="relative flex-1 min-w-[160px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/35 pointer-events-none" strokeWidth={1.75} />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Filter by name, email, trip, topic…"
                className="w-full h-8 pl-8 pr-3 bg-white/[0.04] border border-white/[0.08] rounded-full text-[16px] sm:text-[13px] text-white placeholder:text-white/40 focus:outline-none focus:border-brand-400/40 transition"
                style={{ fontSize: 'max(16px, 0.78rem)' }} /* keep iOS from zooming on focus */
              />
            </div>
          </div>

          {/* The split. min-h-0 lets the panes own their scroll on lg+. */}
          <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[minmax(0,360px)_minmax(0,1fr)] gap-3">
            {/* === LIST === */}
            <div className={`${detailOpenMobile ? 'hidden lg:block' : 'block'} min-h-0`}>
              <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden lg:h-full lg:overflow-y-auto">
                {listLoading && inquiries.length === 0 ? (
                  <div className="p-8 text-center text-[13px] text-white/50">Loading inquiries…</div>
                ) : listError ? (
                  <div className="p-5 text-[12.5px] text-rose-300">{listError}</div>
                ) : visibleInquiries.length === 0 ? (
                  <div className="p-8 text-center text-[13px] text-white/50">
                    {query
                      ? 'Nothing matches that filter.'
                      : statusFilter === 'resolved'
                        ? 'Nothing resolved yet.'
                        : statusFilter === 'overdue'
                          ? 'Nothing overdue — every promise is still inside its window.'
                          : statusFilter === 'dismissed'
                            ? 'Nothing dismissed.'
                            : 'Nothing outstanding — the queue is clear.'}
                  </div>
                ) : (
                  <ul className="divide-y divide-white/[0.05]">
                    {visibleInquiries.map((inq) => (
                      <InquiryRow key={inq.id} inquiry={inq} active={inq.id === selectedId} onSelect={select} />
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* === DETAIL === */}
            <div className={`${detailOpenMobile ? 'block' : 'hidden lg:block'} min-h-0`}>
              {!selected ? (
                <div className="hidden lg:flex h-full min-h-[50vh] items-center justify-center text-center text-[13.5px] text-white/50 rounded-2xl border border-white/[0.05] bg-white/[0.015]">
                  <div className="max-w-xs">
                    <Inbox className="w-8 h-8 mx-auto text-white/20 mb-3" strokeWidth={1.5} />
                    Pick an inquiry to see the customer&apos;s request and their conversation.
                  </div>
                </div>
              ) : (
                <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden lg:h-full flex flex-col">
                  {/* Identity header */}
                  <div className="flex items-center gap-2.5 px-3 sm:px-4 py-3 border-b border-white/[0.06] bg-black/60 backdrop-blur sticky top-0 lg:static z-10 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => select(null)}
                      className="lg:hidden inline-flex items-center justify-center -ml-1 w-9 h-9 rounded-full text-white/70 active:bg-white/10 hover:bg-white/[0.06] transition flex-shrink-0"
                      aria-label="Back to inquiries"
                    >
                      <ChevronLeft className="w-5 h-5" strokeWidth={1.75} />
                    </button>
                    <CustomerDot inquiry={selected} size="w-9 h-9 text-[13px]" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <h2 className="text-[14.5px] font-semibold truncate">{customerName(selected)}</h2>
                        <VipBadge userId={selected.user_id || null} email={selected.user_email || null} />
                        <StatusChip inquiry={selected} className="flex-shrink-0" />
                      </div>
                      <div className="text-[12px] text-white/55 truncate">
                        {inquiryHeadline(selected)}
                        {selected.booking_reference ? (
                          <>
                            <span className="mx-1.5 text-white/25">·</span>
                            <span className="inline-flex items-center gap-1 text-emerald-300/85">
                              <Plane className="w-3 h-3 inline" strokeWidth={1.75} />
                              {selected.booking_reference}
                            </span>
                          </>
                        ) : null}
                      </div>
                    </div>
                    {selected.status === 'open' ? (
                      <button
                        type="button"
                        onClick={claim}
                        disabled={claiming}
                        className="inline-flex items-center gap-1.5 text-[11.5px] font-medium px-2.5 h-8 rounded-full bg-white/[0.04] border border-white/[0.08] text-white/60 hover:text-white transition disabled:opacity-40 flex-shrink-0"
                        title="Mark in progress so two admins don't answer the same customer twice"
                      >
                        <Hand className="w-3.5 h-3.5" strokeWidth={1.75} />
                        <span className="hidden sm:inline">{claiming ? 'Claiming…' : "I'm on it"}</span>
                      </button>
                    ) : null}
                    {['open', 'in_progress'].includes(selected.status) ? (
                      <button
                        type="button"
                        onClick={dismissAsDuplicate}
                        disabled={dismissing}
                        aria-label={dismissArmed ? 'Confirm dismiss — no email sent' : 'Dismiss inquiry'}
                        className={`inline-flex items-center gap-1.5 text-[11.5px] font-medium px-2.5 h-8 rounded-full border transition disabled:opacity-40 flex-shrink-0 ${
                          dismissArmed
                            ? 'bg-red-500/15 border-red-400/30 text-red-200'
                            : 'bg-white/[0.04] border-white/[0.08] text-white/45 hover:text-white/80'
                        }`}
                        title="Erase a duplicate or misfiled inquiry — the customer is NOT emailed and the card disappears from their trip page"
                      >
                        {/* Not an ✕: on a phone this sits where "close" lives,
                            and an icon-only ✕ read as close (TS2MFP). */}
                        <Archive className="w-3.5 h-3.5" strokeWidth={1.75} />
                        {dismissing ? (
                          <span>Dismissing…</span>
                        ) : dismissArmed ? (
                          <>
                            <span className="sm:hidden">Confirm</span>
                            <span className="hidden sm:inline">Tap again — no email sent</span>
                          </>
                        ) : (
                          <span className="hidden sm:inline">Dismiss</span>
                        )}
                      </button>
                    ) : null}
                    {selected.status === 'dismissed' ? (
                      <button
                        type="button"
                        onClick={restoreSelected}
                        disabled={restoring}
                        className="inline-flex items-center gap-1.5 text-[11.5px] font-medium px-2.5 h-8 rounded-full bg-white/[0.04] border border-white/[0.08] text-white/70 hover:text-white transition disabled:opacity-40 flex-shrink-0"
                        title="Put this back on the worklist and on the customer's trip page — no email sent"
                      >
                        <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.75} />
                        <span>{restoring ? 'Restoring…' : 'Restore'}</span>
                      </button>
                    ) : null}
                  </div>

                  {/* Body: conversation + context rail (rail stacks above the
                      conversation below xl — context-first reading order). */}
                  <div ref={conversationRef} className="flex-1 min-h-0 lg:overflow-y-auto">
                    <div className="flex flex-col-reverse xl:flex-row xl:items-start">
                      <div className="min-w-0 flex-1 xl:border-r xl:border-white/[0.05]">
                        <div className="px-3 sm:px-4 pt-3.5 pb-1">
                          {/* The ask, verbatim — always visible above the
                              transcript so the promise being worked is never
                              off-screen. */}
                          <div className="rounded-2xl border border-white/[0.07] bg-white/[0.03] px-3.5 py-3">
                            <div className="text-[10px] uppercase tracking-wider font-medium text-white/35 mb-1">
                              Customer&apos;s request · {formatRelativeTime(selected.created_at)}
                            </div>
                            <p className="text-[14px] text-white/90 leading-relaxed whitespace-pre-wrap">
                              {selected.customer_question}
                            </p>
                          </div>
                        </div>
                        <ConversationThread threadState={threadState} inquiry={selected} />
                      </div>
                      <aside className="xl:w-[300px] flex-shrink-0 p-3 sm:p-4 space-y-3 xl:sticky xl:top-0">
                        <ResolutionCard inquiry={selected} />
                        <DetailsCard inquiry={selected} onOpenTrip={selected.booking_reference ? () => setTripRef(selected.booking_reference) : null} />
                        {isStrictAdmin(user) && ['open', 'in_progress'].includes(selected.status) ? (
                          <ScheduleCard inquiry={selected} onSchedule={scheduleRequest} saving={scheduling} error={scheduleError} />
                        ) : null}
                        {isStrictAdmin(user) && selected.category === 'account_deletion' && ['open', 'in_progress'].includes(selected.status) ? (
                          <AccountDeletionCard inquiry={selected} buildAuthHeader={buildAuthHeader} onDeleted={accountDeleted} />
                        ) : null}
                        <InternalNotesCard inquiry={selected} />
                      </aside>
                    </div>
                  </div>

                  {tripRef && (
                    <InquiryTripModal
                      reference={tripRef}
                      currentUser={user}
                      onClose={() => setTripRef(null)}
                    />
                  )}

                  {/* Composer — resolving sends the note verbatim to the
                      customer's trip card and emails it to them. */}
                  {showComposer ? (
                    <div className="border-t border-white/[0.06] bg-black/70 backdrop-blur px-3 sm:px-4 py-3 sticky bottom-0 lg:static flex-shrink-0">
                      {actionError ? (
                        <div className="mb-2 rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-1.5 text-[12px] text-red-200/90">
                          {actionError}
                        </div>
                      ) : null}
                      {replyNotice ? (
                        <div className="mb-2 rounded-lg border border-white/10 bg-white/[0.04] px-3 py-1.5 text-[12px] text-white/75">
                          {replyNotice}
                        </div>
                      ) : null}
                      {selected.customer_replied_at ? (
                        <div className="mb-2 flex items-center gap-2 rounded-lg border border-sky-400/20 bg-sky-500/[0.08] px-3 py-1.5 text-[12px] text-sky-100/90">
                          <Reply className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={1.75} />
                          <span className="min-w-0">
                            {customerName(selected)} replied {formatRelativeTime(selected.customer_replied_at)} and is waiting on the team.
                          </span>
                          <button
                            type="button"
                            onClick={clearReply}
                            disabled={saving}
                            className="ml-auto flex-shrink-0 text-[11.5px] font-medium px-2.5 h-7 rounded-full bg-white/[0.06] border border-white/[0.12] text-white/80 hover:bg-white/[0.1] transition disabled:opacity-40"
                          >
                            No reply needed
                          </button>
                        </div>
                      ) : null}
                      {waitingOnCustomer && !draftLoading ? (
                        <div className="mb-2 rounded-lg border border-white/[0.08] bg-white/[0.03] px-3 py-1.5 text-[12px] text-white/60">
                          Waiting on {customerName(selected)} to answer the team&apos;s last message.
                        </div>
                      ) : draftsAsked.has(selected.id) && (draftLoading || draftError || selected.stephanie_draft) ? (
                        <div className="mb-2 rounded-xl border border-violet-400/20 bg-violet-500/[0.06] px-3.5 py-2.5">
                          <div className="flex items-center gap-2 mb-1.5">
                            <Sparkles className="w-3.5 h-3.5 text-violet-200/80 flex-shrink-0" strokeWidth={1.75} />
                            <span className="text-[12px] font-medium text-violet-100/90">Stephanie&apos;s draft</span>
                            {selected.stephanie_draft && !draftLoading ? (
                              <span className={`text-[11px] ${selected.stephanie_draft_ready ? 'text-emerald-300/85' : 'text-amber-200/85'}`}>
                                {selected.stephanie_draft_ready ? 'Ready to send' : 'Check first'}
                              </span>
                            ) : null}
                            <div className="ml-auto flex items-center gap-1.5 flex-shrink-0">
                              {selected.stephanie_draft && !draftLoading ? (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setNoteDraft(selected.stephanie_draft);
                                    noteDraftsRef.current.set(selected.id, selected.stephanie_draft);
                                  }}
                                  className="text-[11.5px] font-medium px-2.5 h-7 rounded-full bg-violet-500/15 border border-violet-400/30 text-violet-100 hover:bg-violet-500/25 transition"
                                >
                                  Use draft
                                </button>
                              ) : null}
                              <button
                                type="button"
                                onClick={() => askStephanie(selected, { force: true })}
                                disabled={draftLoading}
                                className="text-[11.5px] font-medium px-2.5 h-7 rounded-full bg-white/[0.05] border border-white/[0.12] text-white/75 hover:bg-white/[0.09] transition disabled:opacity-40"
                              >
                                {draftLoading ? 'Drafting…' : 'Redraft'}
                              </button>
                            </div>
                          </div>
                          {draftLoading ? (
                            <p className="text-[12.5px] text-white/55">Stephanie is drafting a reply…</p>
                          ) : draftError && !selected.stephanie_draft ? (
                            <p className="text-[12.5px] text-red-200/90">{draftError}</p>
                          ) : (
                            <>
                              <p className="text-[13.5px] text-white/90 whitespace-pre-wrap leading-relaxed max-h-48 overflow-y-auto">
                                {selected.stephanie_draft}
                              </p>
                              {selected.stephanie_draft_notes ? (
                                <p className="mt-1.5 text-[12px] text-white/55 leading-snug">
                                  <span className="text-white/40">Her notes for the team: </span>
                                  {selected.stephanie_draft_notes}
                                </p>
                              ) : null}
                            </>
                          )}
                        </div>
                      ) : admin ? (
                        <div className="mb-2 flex justify-end">
                          <button
                            type="button"
                            onClick={() => askStephanie(selected)}
                            className="inline-flex items-center gap-1.5 text-[11.5px] font-medium px-2.5 h-7 rounded-full bg-violet-500/10 border border-violet-400/25 text-violet-100/90 hover:bg-violet-500/20 transition"
                          >
                            <Sparkles className="w-3.5 h-3.5" strokeWidth={1.75} />
                            Draft with Stephanie
                          </button>
                        </div>
                      ) : null}
                      <textarea
                        value={noteDraft}
                        onChange={(e) => {
                          setNoteDraft(e.target.value);
                          if (selected?.id) noteDraftsRef.current.set(selected.id, e.target.value);
                        }}
                        rows={3}
                        placeholder="Write directly to the customer…"
                        className="w-full rounded-xl bg-white/[0.04] border border-white/[0.09] px-3.5 py-2.5 text-base md:text-[14px] text-white/95 placeholder:text-white/35 focus:outline-none focus:border-brand-400/40 resize-y"
                      />
                      <div className="flex items-center gap-2.5 mt-2">
                        {stillOpen ? (
                          <button
                            type="button"
                            onClick={resolve}
                            disabled={saving || !noteDraft.trim()}
                            className="inline-flex items-center gap-1.5 text-[12.5px] font-medium px-3.5 h-9 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-200 hover:bg-emerald-500/25 transition disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <Send className="w-3.5 h-3.5" strokeWidth={1.75} />
                            {saving ? 'Sending…' : 'Send & resolve'}
                          </button>
                        ) : null}
                        <button
                          type="button"
                          onClick={reply}
                          disabled={saving || !noteDraft.trim()}
                          className="inline-flex items-center gap-1.5 text-[12.5px] font-medium px-3.5 h-9 rounded-full bg-white/[0.05] border border-white/[0.12] text-white/85 hover:bg-white/[0.09] transition disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {!stillOpen && saving ? 'Sending…' : stillOpen ? 'Send, keep open' : 'Send'}
                        </button>
                        <span className="text-[11.5px] text-white/45 leading-tight">
                          Posts in their chat as &ldquo;{sendsAsStephanie ? 'Stephanie' : 'Defera team'}&rdquo; and emails them a link to reply there.{stillOpen ? ' Resolving also puts it on their trip page.' : ''}
                        </span>
                      </div>
                    </div>
                  ) : actionError ? (
                    <div className="border-t border-white/[0.06] px-3 sm:px-4 py-2.5 text-[12px] text-red-200/90 flex-shrink-0">
                      {actionError}
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Undo — a dismissed inquiry leaves the list instantly, so say what
          just happened and offer the way back while it's still fresh. */}
      {undoDismiss ? (
        <div
          role="status"
          aria-live="polite"
          className="fixed left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-md flex items-center gap-3 rounded-full border border-white/10 bg-[#10131d]/95 backdrop-blur px-4 py-2.5 shadow-lg shadow-black/40"
          style={{ bottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
        >
          <span className="flex-1 min-w-0 text-[12.5px] text-white/85 truncate">
            {undoDismiss.error
              ? `Couldn't undo: ${undoDismiss.error}`
              : `Dismissed ${(undoDismiss.inquiry.user_first_name || '').trim() || 'this'}'s ${undoDismiss.inquiry.booking_reference || 'inquiry'} — no email sent.`}
          </span>
          {undoDismiss.error ? (
            <button
              type="button"
              onClick={() => setUndoDismiss(null)}
              className="text-[12.5px] font-medium text-white/60 hover:text-white flex-shrink-0"
            >
              Close
            </button>
          ) : (
            <button
              type="button"
              onClick={undoLastDismiss}
              disabled={undoing}
              className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-sky-300 hover:text-sky-200 disabled:opacity-50 flex-shrink-0"
            >
              <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.75} />
              {undoing ? 'Undoing…' : 'Undo'}
            </button>
          )}
        </div>
      ) : null}
    </>
  );
}
